import type {
  ProviderConfig,
  SdpNegotiator,
  SessionEnder,
  SessionEventSender,
  SessionGrant,
  SessionRequester,
} from "@microsoft/voice-widget-core-client";
import type { AttemptContext, TelemetryEmitter } from "./telemetry.js";
import { token, validateSessionId } from "./telemetry.js";

/**
 * Telemetry context threaded into the auth-endpoint factories so each broker HTTP hop
 * can emit `broker_request` / `broker_response` (and the negotiate hop `session_id_resolved`)
 * against the current connection attempt. Optional and trailing so the factories stay
 * backward-compatible for callers that do not instrument.
 */
export interface AuthEndpointTelemetryContext {
  ctx: AttemptContext;
  emitter: TelemetryEmitter;
  generation: number;
  widgetInstanceId?: string;
}

/**
 * Consume a broker control-hop response body (`/update`, `/end`) to close the telemetry
 * duration boundary, then best-effort parse it for an error code and an echoed correlation
 * ID. The body is NON-authoritative here: an empty or malformed 200 — or a `text()` that
 * throws — degrades telemetry only (the fields are omitted) and never fails the operation.
 */
async function readBrokerBody(
  res: Response,
  telemetry: AuthEndpointTelemetryContext | undefined,
): Promise<{ errorCode?: string; correlationMismatch?: true }> {
  let code: unknown;
  let echoedCorrelationId: unknown;
  try {
    const parsed = JSON.parse(await res.text()) as {
      error?: { code?: unknown };
      correlationId?: unknown;
    };
    code = parsed.error?.code;
    echoedCorrelationId = parsed.correlationId;
  } catch {
    // Empty/malformed body, or a body-read failure: telemetry-only degradation.
  }
  const errorCode = token(code);
  const correlationMismatch =
    typeof echoedCorrelationId === "string" && echoedCorrelationId !== telemetry?.ctx.correlationId
      ? true
      : undefined;
  return {
    ...(errorCode !== undefined ? { errorCode } : {}),
    ...(correlationMismatch ? { correlationMismatch } : {}),
  };
}

function childEndpoint(endpoint: string, child: string): string {
  const base = typeof location !== "undefined" ? location.href : "http://localhost/";
  const url = new URL(endpoint, base);
  url.pathname = `${url.pathname.replace(/\/+$/, "")}/${child}`;
  return url.toString();
}

/**
 * Builds an SdpNegotiator that POSTs the browser's SDP offer to a customer
 * auth-endpoint / broker and returns the SDP answer. Session logic (not UI):
 * the browser stays media-only; the broker holds the credential.
 *
 * With a telemetry context it emits `broker_request` before the POST and `broker_response`
 * once the body is consumed (the duration boundary closes at `res.json()` on success or
 * `res.text()` on failure), tags a non-ok response as the `broker` failure stage, and
 * records the broker-authored `sessionId` via `session_id_resolved`.
 */
export function makeAuthEndpointNegotiator(
  authEndpoint: string,
  provider: string,
  config: ProviderConfig,
  widgetId: string | undefined,
  fetchCredentials: RequestCredentials,
  telemetry?: AuthEndpointTelemetryContext,
): SdpNegotiator {
  const requestSession = makeAuthEndpointSessionRequester(
    authEndpoint,
    provider,
    config,
    widgetId,
    fetchCredentials,
    telemetry,
    "negotiate",
  );
  return async (offer: string) => {
    const grant = await requestSession({ sdpOffer: offer });
    if (typeof grant.sdpAnswer !== "string" || grant.sdpAnswer.length === 0) {
      throw new Error("session response did not include a non-empty sdpAnswer");
    }
    return {
      sdpAnswer: grant.sdpAnswer,
      sessionId: grant.sessionId,
      expiresAt: grant.expiresAt,
      ...(grant.clientToolPolicy ? { clientToolPolicy: grant.clientToolPolicy } : {}),
    };
  };
}

const RESERVED_SESSION_REQUEST_FIELDS = new Set([
  "provider",
  "config",
  "widgetId",
  "origin",
  "correlationId",
  "widgetInstanceId",
]);

/**
 * Build a provider-neutral auth-endpoint client.
 *
 * The stable envelope identifies the provider and carries host-owned config;
 * provider-specific request fields are added at the top level for backward
 * compatibility with the existing Voice Live `{ sdpOffer }` broker contract.
 */
export function makeAuthEndpointSessionRequester(
  authEndpoint: string,
  provider: string,
  config: ProviderConfig,
  widgetId: string | undefined,
  fetchCredentials: RequestCredentials,
  telemetry?: AuthEndpointTelemetryContext,
  sessionIdSource: "grant" | "negotiate" = "grant",
): SessionRequester {
  return async (request = {}) => {
    for (const key of Object.keys(request)) {
      if (RESERVED_SESSION_REQUEST_FIELDS.has(key)) {
        throw new Error(`provider session request must not override reserved field "${key}"`);
      }
    }
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_request",
      { endpoint: "session" },
      telemetry.generation,
    );
    const start = performance.now();
    let res: Response;
    try {
      res = await fetch(authEndpoint, {
        method: "POST",
        // Attach the site's cookies so a broker on the customer's own domain can
        // authenticate the end-user from their existing session. For a
        // CROSS-ORIGIN broker this also requires Access-Control-Allow-Credentials: true
        // with a specific (non-"*") Access-Control-Allow-Origin on BOTH the POST and
        // the OPTIONS preflight, else the browser blocks the request.
        credentials: fetchCredentials,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...request,
          provider,
          ...(widgetId ? { widgetId } : {}),
          config,
          origin: typeof location !== "undefined" ? location.origin : "",
          ...(telemetry
            ? {
                correlationId: telemetry.ctx.correlationId,
                widgetInstanceId: telemetry.widgetInstanceId,
              }
            : {}),
        }),
      });
    } catch (err) {
      // A rejected fetch (offline / DNS / connection-refused / CORS / TLS) still settles the
      // POST as a failure: emit broker_response with the elapsed duration and tag
      // the built-in auth-endpoint hop as the `broker` stage before rethrowing. `status` is
      // omitted — there was no HTTP response.
      const durationMs = Math.round(performance.now() - start);
      telemetry?.emitter.emit(
        telemetry.ctx,
        "broker_response",
        { endpoint: "session", ok: false, durationMs, errorCode: "network_error" },
        telemetry.generation,
      );
      telemetry?.emitter.emitFailureOnce(
        telemetry.ctx,
        "broker",
        { code: "network_error" },
        telemetry.generation,
      );
      throw err;
    }
    if (!res.ok) {
      let detail: { code?: unknown; message?: unknown } | undefined;
      let echoedCorrelationId: unknown;
      try {
        const body = JSON.parse(await res.text()) as {
          error?: { code?: unknown; message?: unknown };
          correlationId?: unknown;
        };
        detail = body.error;
        echoedCorrelationId = body.correlationId;
      } catch {
        // Non-JSON broker errors (or a body-read failure) retain the status-only fallback
        // below and degrade telemetry only.
      }
      const durationMs = Math.round(performance.now() - start);
      const code = typeof detail?.code === "string" ? detail.code : undefined;
      const message = typeof detail?.message === "string" ? detail.message : undefined;
      const correlationMismatch =
        typeof echoedCorrelationId === "string" &&
        echoedCorrelationId !== telemetry?.ctx.correlationId
          ? true
          : undefined;
      telemetry?.emitter.emit(
        telemetry.ctx,
        "broker_response",
        {
          endpoint: "session",
          ok: false,
          status: res.status,
          durationMs,
          errorCode: token(code),
          ...(correlationMismatch ? { correlationMismatch } : {}),
        },
        telemetry.generation,
      );
      // Tag the built-in auth-endpoint rejection as the `broker` stage. The per-attempt
      // latch dedupes the outer `provider` re-emit from createVoiceAgent's connect catch.
      telemetry?.emitter.emitFailureOnce(telemetry.ctx, "broker", { code }, telemetry.generation);
      const description = [code, message].filter(Boolean).join(": ");
      throw new Error(description || `session request failed: ${res.status}`);
    }
    const parsed: unknown = await res.json();
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("session response must be a JSON object");
    }
    const body = parsed as SessionGrant;
    const durationMs = Math.round(performance.now() - start);
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_response",
      { endpoint: "session", ok: true, status: res.status, durationMs },
      telemetry.generation,
    );
    if (telemetry) {
      // The negotiate result is a sessionId source: record it on the attempt and emit
      // session_id_resolved only when a valid, changed id arrives. The value returned to the
      // provider stays the raw broker value — telemetry never alters correctness.
      const resolvedSessionId = validateSessionId(body.sessionId);
      if (resolvedSessionId !== undefined && resolvedSessionId !== telemetry.ctx.sessionId) {
        const previousSessionId = telemetry.ctx.sessionId;
        telemetry.ctx.sessionId = resolvedSessionId;
        telemetry.emitter.emit(
          telemetry.ctx,
          "session_id_resolved",
          { source: sessionIdSource, previousSessionId },
          telemetry.generation,
        );
      }
    }
    return body;
  };
}

/** Build the broker control-event hop (`POST {authEndpoint}/update`). */
export function makeAuthEndpointSessionEventSender(
  authEndpoint: string,
  fetchCredentials: RequestCredentials,
  telemetry?: AuthEndpointTelemetryContext,
): SessionEventSender {
  const updateEndpoint = childEndpoint(authEndpoint, "update");
  return async (sessionId, event) => {
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_request",
      { endpoint: "update" },
      telemetry.generation,
    );
    const start = performance.now();
    let res: Response;
    try {
      res = await fetch(updateEndpoint, {
        method: "POST",
        credentials: fetchCredentials,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          sessionId,
          event,
          ...(telemetry
            ? {
                correlationId: telemetry.ctx.correlationId,
                widgetInstanceId: telemetry.widgetInstanceId,
              }
            : {}),
        }),
      });
    } catch (err) {
      // A rejected fetch still settles this hop as a failure: emit broker_response
      // with the elapsed duration before rethrowing. `status` is omitted (no HTTP response).
      const durationMs = Math.round(performance.now() - start);
      telemetry?.emitter.emit(
        telemetry.ctx,
        "broker_response",
        { endpoint: "update", ok: false, durationMs, errorCode: "network_error" },
        telemetry.generation,
      );
      throw err;
    }
    // Consume the body to close the duration boundary; a malformed/empty 200 must not fail
    // this mid-session hop. readBrokerBody keeps the parse best-effort.
    const bodyMeta = await readBrokerBody(res, telemetry);
    const durationMs = Math.round(performance.now() - start);
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_response",
      { endpoint: "update", ok: res.ok, status: res.status, durationMs, ...bodyMeta },
      telemetry.generation,
    );
    if (!res.ok) throw new Error(`session update failed: ${res.status}`);
  };
}

/** Build the broker teardown hop (`POST {authEndpoint}/end`). */
export function makeAuthEndpointSessionEnder(
  authEndpoint: string,
  fetchCredentials: RequestCredentials,
  telemetry?: AuthEndpointTelemetryContext,
): SessionEnder {
  const endEndpoint = childEndpoint(authEndpoint, "end");
  return async (sessionId) => {
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_request",
      { endpoint: "end" },
      telemetry.generation,
    );
    const start = performance.now();
    let res: Response;
    try {
      res = await fetch(endEndpoint, {
        method: "POST",
        credentials: fetchCredentials,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          sessionId,
          ...(telemetry
            ? {
                correlationId: telemetry.ctx.correlationId,
                widgetInstanceId: telemetry.widgetInstanceId,
              }
            : {}),
        }),
      });
    } catch (err) {
      // A rejected fetch still settles this hop as a failure: emit broker_response
      // with the elapsed duration before rethrowing. `status` is omitted (no HTTP response).
      const durationMs = Math.round(performance.now() - start);
      telemetry?.emitter.emit(
        telemetry.ctx,
        "broker_response",
        { endpoint: "end", ok: false, durationMs, errorCode: "network_error" },
        telemetry.generation,
      );
      throw err;
    }
    // Consume the body to close the duration boundary; teardown tolerates a malformed/empty
    // 200. Best-effort telemetry parse happens inside readBrokerBody.
    const bodyMeta = await readBrokerBody(res, telemetry);
    const durationMs = Math.round(performance.now() - start);
    telemetry?.emitter.emit(
      telemetry.ctx,
      "broker_response",
      { endpoint: "end", ok: res.ok, status: res.status, durationMs, ...bodyMeta },
      telemetry.generation,
    );
    if (!res.ok) throw new Error(`session end failed: ${res.status}`);
  };
}
